import React, { useEffect, useState } from "react"
import { Container, Row, Col, Button, Card, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../components/auth/useAuth"
import { getPolicies } from "../actions/other/policiesActions"
import { getCompanias } from "../actions/other/companiasActions"
import { RootState, AppDispatch } from "../store"
import { CustomDataTable } from "../components/design/Datatables/CustomDataTable"
import Loader from "../components/loaders/Loader"

export interface Policy {
    _id?: string
    id?: string
    numero?: number
    nifCliente?: string
    nombreCliente?: string
    fechaEfecto?: string
    fechaLimiteTemporal?: string
    nombreCompania?: string
    idEstado?: string
    riesgo?: string
    subramo?: string
    formaPago?: string
}

export default function Policies() {
    const { t } = useTranslation()
    const dispatch: AppDispatch = useDispatch()
    const { clientauth } = useAuth()
    const navigate = useNavigate()

    const { policies, total, loading } = useSelector((state: RootState) => state.policies)

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(25)
    const [search, setSearch] = useState<Object>({})
    const [sort, setSort] = useState<any>(null)

    const [numero, setNumero] = useState('')
    const [nifCliente, setNifCliente] = useState('')
    const [nombreCliente, setNombreCliente] = useState('')
    const [fechaEfecto, setFechaEfecto] = useState('')
    const [compania, setCompania] = useState('')
    const [estado, setEstado] = useState('')

    const [totalRows, setTotalRows] = useState(0)

    const { companias } = useSelector((state: RootState) => state.companias)

    useEffect(() => {
        if (clientauth) {
            dispatch(getCompanias() as any)
        }
    // eslint-disable-next-line
    }, [dispatch, clientauth])

    useEffect(() => {
        setTotalRows(total || 0)
    }, [total])

    const handleSearch = () => {
        dispatch(getPolicies(1, 999999, search) as any)
    }

    const selectPolicy = (policy: Policy) => {
        navigate(`${policy.id}`)
    }

    const columnsPolicy = [
        {
            name: t("policyTranslate.numero"),
            id: "numero",
            width: "150px",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                {policy?.numero || "—"}
                </div>
            ),
            selector: (policy: Policy) => policy?.numero || "—",
            sortable: true,
        },
        {
            name: t("policyTranslate.nombreCliente"),
            id: "nombreCliente",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                    {policy?.nombreCliente || "—"}
                </div>
            ),
            selector: (policy: Policy) => policy?.nombreCliente || "—",
            sortable: true,
        },{
            name: t("policyTranslate.fechaEfecto"),
            id: "fechaEfecto",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                    {new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Madrid' }).format(new Date(policy?.fechaEfecto || "—"))}
                </div>
            ),
            selector: (policy: Policy) => policy?.fechaEfecto || "—",
            sortable: true,
        },{
            name: t("policyTranslate.compania"),
            id: "nombreCompania",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                    {policy?.nombreCompania || "—"}
                </div>
            ),
            selector: (policy: Policy) => policy?.nombreCompania || "—",
            sortable: true,
        },{
            name: t("policyTranslate.estado"),
            id: "idEstado",
            cell: (policy: Policy) => (
                <div className="cursor" onClick={() => selectPolicy(policy)}>
                    {policy?.idEstado === "A" ?
                        t("policyTranslate.anulado") :
                        policy?.idEstado === "V" ?
                            t("policyTranslate.enVigor") :
                            policy?.idEstado === "S" ?
                                t("policyTranslate.suspension") :
                                "—"
                    }
                </div>
            ),
            selector: (policy: Policy) => policy?.idEstado || "—",
            sortable: true,
        },{
            name: t("policyTranslate.riesgo"),
            id: "riesgo",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                    {policy?.riesgo || "—"}
                </div>
            ),
            selector: (policy: Policy) => policy?.riesgo || "—",
            sortable: true,
        },{
            name: t("policyTranslate.subramo"),
            id: "subramo",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                    {policy?.nombreCompania && policy?.subramo
                        ? `${policy.nombreCompania} - ${policy.subramo}`
                        : policy?.subramo || policy?.nombreCompania || "—"}
                </div>
            ),
            selector: (policy: Policy) => policy?.subramo || "—",
            sortable: true,
        },{
            name: t("policyTranslate.formaPago"),
            id: "formaPago",
            cell: (policy: Policy) => (
                <div className="cursor text-start" onClick={() => selectPolicy(policy)}>
                    {policy?.formaPago || "—"}
                </div>
            ),
            selector: (policy: Policy) => policy?.formaPago || "—",
            sortable: true,
        }
    ]

    return (
        <Container fluid className='p-1 py-5 p-md-5'>
            <Row>
                <Col xs={12}>
                    <h1>{t('menus.policies')}</h1>
                </Col>
            </Row>
            <Row>
                <Col xs={12} className='my-3'>
                    <Card>
                        <Card.Body>
                            <Row className="justify-content-between">
                                <Col xs={10}>
                                    <Row>
                                        <Col xs={6} lg={3} className="mb-3">
                                            <Form.Control
                                                type="text"
                                                placeholder={t("policyTranslate.numPoliza")}
                                                value={numero || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setNumero(e.target.value);
                                                    setSearch({...search, numero: e.target.value});
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({...search, numero: numero});
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={6} lg={3} className="mb-3">
                                            <Form.Control
                                                type="text"
                                                placeholder={t("policyTranslate.fechaEfecto")}
                                                value={fechaEfecto || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setFechaEfecto(e.target.value);
                                                    setSearch({...search, fechaEfecto: e.target.value});
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({...search, fechaEfecto: fechaEfecto});
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={6} lg={3} className="mb-3">
                                            <Form.Control
                                                className="text-uppercase"
                                                type="text"
                                                placeholder={t("policyTranslate.nifCliente")}
                                                value={nifCliente || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const val = e.target.value.toUpperCase();
                                                    setNifCliente(val);
                                                    setSearch({...search, nifCliente: val});
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({...search, nifCliente: nifCliente});
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={6} lg={3} className="mb-3">
                                            <Form.Control
                                                className="text-uppercase"
                                                type="text"
                                                placeholder={t("policyTranslate.nombreCliente")}
                                                value={nombreCliente || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const val = e.target.value.toUpperCase();
                                                    setNombreCliente(val);
                                                    setSearch({...search, nombreCliente: val});
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch({...search, nombreCliente: nombreCliente});
                                                        setPage(1);
                                                        handleSearch();
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} lg={6}>
                                            <Form.Group className="mb-3">
                                                <select className="form-control me-2" value={compania || ""} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                    setCompania(e.target.value);
                                                    setPage(1);
                                                    setSearch({...search, compania: parseInt(e.target.value)});
                                                }}>
                                                    <option value="">- Seleccione {t("policyTranslate.compania")} -</option>
                                                    {companias?.map((compania) => (
                                                        <option key={compania.ebrokerId} value={compania.ebrokerId}>{compania.name}</option>
                                                    ))}
                                                </select>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} lg={6}>
                                            <Form.Group className="mb-3">
                                                <select className="form-control me-2" value={estado || ""} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                    setEstado(e.target.value);
                                                    setPage(1);
                                                    setSearch({...search, estado: e.target.value});
                                                }}>
                                                    <option value="">- Seleccione {t("policyTranslate.estado")} -</option>
                                                    <option value="A">{t("policyTranslate.anulado")}</option>
                                                    <option value="V">{t("policyTranslate.enVigor")}</option>
                                                    <option value="S">{t("policyTranslate.suspension")}</option>
                                                </select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xs={2} className='text-end'>
                                    <Button className="button-search" onClick={handleSearch}>
                                        <i className="fas fa-search" />
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            {loading ? (
                                <Loader />
                            ) : policies?.length > 0 ? (
                                <CustomDataTable
                                    columns={columnsPolicy}
                                    data={policies}
                                    pagination
                                />
                            ) : (
                                <p className="text-center m-0">{t("policyTranslate.thereisnotpolicy")}</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}